import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { Stage, Layer, Line, Text, Rect, Group } from "react-konva";
import type { KonvaEventObject } from "konva/lib/Node";
import type { CenterlineData } from "./useTunnelCenterline";
import { AppToolbarButton } from "./WhatsNew";
import {
  type ProfileAssignment,
  getProfileAtStation,
} from "./tunnelProfileShapes";
import {
  type WeaknessZoneLine,
  type TunnelPoint,
  loadLines,
  saveLines,
} from "./weaknessZones";
import { useProject } from "./ProjectContext";

interface UnwrappedProfileProps {
  data: CenterlineData;
  profileAssignments: ProfileAssignment[];
  onBack: () => void;
}

type Tool = "select" | "draw";

/**
 * Utbrettsprofil — unwrapped tunnel profile in 2D.
 *
 * X-axis = station (chainage along tunnel)
 * Y-axis = perimeter distance around the cross-section profile
 *
 * Lines are stored in tunnel-local coordinates (station, perimeter) and
 * persisted to localStorage. They can be projected to 3D via weaknessZones.ts.
 */
export function UnwrappedProfile({ data, profileAssignments, onBack }: UnwrappedProfileProps) {
  const project = useProject();
  const containerRef = useRef<HTMLDivElement>(null);
  const [stageSize, setStageSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Resize handling
  useState(() => {
    const handler = () => setStageSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  });

  const RING_SPACING = 10;
  const PADDING = 80;

  // Drawing state — persisted lines in tunnel coordinates
  const [tool, setTool] = useState<Tool>("draw");
  const [lines, setLines] = useState<WeaknessZoneLine[]>(() => loadLines(project.id));
  const [currentLinePixels, setCurrentLinePixels] = useState<number[] | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const isDrawing = useRef(false);

  // Persist lines to localStorage whenever they change
  useEffect(() => {
    saveLines(project.id, lines);
  }, [lines]);

  // Compute unwrapped profile data
  const unwrapped = useMemo(() => {
    const totalLength = data.totalLength;
    const ringCount = Math.floor(totalLength / RING_SPACING) + 1;

    const rings: { station: number; perimeter: number; perimeterSegments: number[]; color: string }[] = [];
    let maxPerimeter = 0;

    for (let i = 0; i < ringCount; i++) {
      const station = i * RING_SPACING;
      const profile = getProfileAtStation(station, profileAssignments);
      const pts = profile.points;

      const segments: number[] = [0];
      let cumDist = 0;
      for (let j = 1; j < pts.length; j++) {
        const dx = pts[j].x - pts[j - 1].x;
        const dy = pts[j].y - pts[j - 1].y;
        cumDist += Math.sqrt(dx * dx + dy * dy);
        segments.push(cumDist);
      }

      maxPerimeter = Math.max(maxPerimeter, cumDist);
      rings.push({ station, perimeter: cumDist, perimeterSegments: segments, color: profile.color });
    }

    return { rings, maxPerimeter, totalLength };
  }, [data, profileAssignments]);

  // Compute scale to fit the view
  const drawWidth = stageSize.width - PADDING * 2;
  const drawHeight = stageSize.height - PADDING * 2 - 40;
  const scaleX = drawWidth / unwrapped.totalLength;
  const scaleY = drawHeight / unwrapped.maxPerimeter;
  const drawAreaTop = PADDING + 40;

  // Convert pixel coords to tunnel coords (station, perimeter)
  const pixelToTunnel = useCallback((px: number, py: number): TunnelPoint => {
    const station = (px - PADDING) / scaleX;
    const perimeter = (drawAreaTop + drawHeight - py) / scaleY;
    return { station, perimeter };
  }, [scaleX, scaleY, drawHeight, drawAreaTop]);

  // Convert tunnel coords to pixel coords
  const tunnelToPixel = useCallback((pt: TunnelPoint): { x: number; y: number } => ({
    x: PADDING + pt.station * scaleX,
    y: drawAreaTop + drawHeight - pt.perimeter * scaleY,
  }), [scaleX, scaleY, drawHeight, drawAreaTop]);

  // Convert a persisted line (tunnel coords) to flat pixel array for Konva
  const lineToPixels = useCallback((line: WeaknessZoneLine): number[] => {
    return line.points.flatMap((pt) => {
      const px = tunnelToPixel(pt);
      return [px.x, px.y];
    });
  }, [tunnelToPixel]);

  // Clamp point to draw area
  const clampToDrawArea = useCallback((x: number, y: number) => ({
    x: Math.max(PADDING, Math.min(PADDING + drawWidth, x)),
    y: Math.max(drawAreaTop, Math.min(drawAreaTop + drawHeight, y)),
  }), [drawWidth, drawHeight, drawAreaTop]);

  // Drawing handlers
  const handleMouseDown = useCallback((e: KonvaEventObject<MouseEvent>) => {
    if (tool !== "draw") return;
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const clamped = clampToDrawArea(pos.x, pos.y);
    isDrawing.current = true;
    setCurrentLinePixels([clamped.x, clamped.y]);
    setSelectedLineId(null);
  }, [tool, clampToDrawArea]);

  const handleMouseMove = useCallback((e: KonvaEventObject<MouseEvent>) => {
    if (!isDrawing.current || tool !== "draw") return;
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const clamped = clampToDrawArea(pos.x, pos.y);
    setCurrentLinePixels((prev) => prev ? [...prev, clamped.x, clamped.y] : null);
  }, [tool, clampToDrawArea]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing.current || !currentLinePixels) return;
    isDrawing.current = false;
    if (currentLinePixels.length >= 4) {
      // Convert pixel points to tunnel coordinates for storage
      const tunnelPoints: TunnelPoint[] = [];
      for (let i = 0; i < currentLinePixels.length; i += 2) {
        tunnelPoints.push(pixelToTunnel(currentLinePixels[i], currentLinePixels[i + 1]));
      }
      const newLine: WeaknessZoneLine = {
        id: crypto.randomUUID(),
        points: tunnelPoints,
        color: "#ff4444",
        createdAt: Date.now(),
      };
      setLines((prev) => [...prev, newLine]);
    }
    setCurrentLinePixels(null);
  }, [currentLinePixels, pixelToTunnel]);

  // Delete selected line
  const handleDelete = useCallback(() => {
    if (selectedLineId === null) return;
    setLines((prev) => prev.filter((l) => l.id !== selectedLineId));
    setSelectedLineId(null);
  }, [selectedLineId]);

  // Station tick marks every 100m
  const stationTicks: number[] = [];
  for (let s = 0; s <= unwrapped.totalLength; s += 100) {
    stationTicks.push(s);
  }

  // Cursor position for coordinate display
  const [cursorInfo, setCursorInfo] = useState<TunnelPoint | null>(null);
  const handleStageMouseMove = useCallback((e: KonvaEventObject<MouseEvent>) => {
    handleMouseMove(e);
    const stage = e.target.getStage();
    if (!stage) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    if (pos.x >= PADDING && pos.x <= PADDING + drawWidth &&
        pos.y >= drawAreaTop && pos.y <= drawAreaTop + drawHeight) {
      setCursorInfo(pixelToTunnel(pos.x, pos.y));
    } else {
      setCursorInfo(null);
    }
  }, [handleMouseMove, drawWidth, drawHeight, drawAreaTop, pixelToTunnel]);

  const btnStyle = (active: boolean) => ({
    background: active ? "#4a7aff" : "#333",
    color: "#eee",
    border: `1px solid ${active ? "#6a9aff" : "#555"}`,
    borderRadius: "4px",
    padding: "0.4rem 0.8rem",
    cursor: "pointer" as const,
    fontSize: "0.85rem",
  });

  return (
    <div
      ref={containerRef}
      style={{
        width: "100vw",
        height: "100vh",
        background: "#1a1a2e",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Header bar */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        padding: "0.8rem 1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontFamily: "system-ui",
        color: "#eee",
        background: "rgba(0,0,0,0.4)",
        zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <button onClick={onBack} style={btnStyle(false)}>
            &larr; Tilbake til 3D
          </button>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Utbrettsprofil</h2>

          <div style={{ width: 1, height: 24, background: "#555" }} />

        <button onClick={() => { setTool("select"); setSelectedLineId(null); }} style={btnStyle(tool === "select")}>
          Velg
        </button>
        <button onClick={() => { setTool("draw"); setSelectedLineId(null); }} style={btnStyle(tool === "draw")}>
          Tegn linje
        </button>

        {selectedLineId !== null && (
          <button onClick={handleDelete} style={{ ...btnStyle(false), background: "#8a2222", borderColor: "#aa4444" }}>
            Slett valgt
          </button>
        )}

        {lines.length > 0 && (
          <span style={{ fontSize: "0.8rem", opacity: 0.5 }}>
            {lines.length} linje{lines.length !== 1 ? "r" : ""}
          </span>
        )}

        <div style={{ flex: 1 }} />

        <span style={{ fontSize: "0.8rem", opacity: 0.5, fontFamily: "monospace" }}>
          {cursorInfo
            ? `St. ${cursorInfo.station.toFixed(1)} m | Per. ${cursorInfo.perimeter.toFixed(1)} m`
            : "\u00A0"}
        </span>
        </div>
        <AppToolbarButton />
      </div>

      {/* Konva canvas */}
      <Stage
        width={stageSize.width}
        height={stageSize.height}
        onMouseDown={handleMouseDown}
        onMousemove={handleStageMouseMove}
        onMouseup={handleMouseUp}
        onMouseLeave={() => { handleMouseUp(); setCursorInfo(null); }}
        style={{ cursor: tool === "draw" ? "crosshair" : "default" }}
      >
        <Layer>
          <Rect x={0} y={0} width={stageSize.width} height={stageSize.height} fill="#1a1a2e" />

          <Rect
            x={PADDING}
            y={drawAreaTop}
            width={drawWidth}
            height={drawHeight}
            fill="#0d0d1a"
            stroke="#333"
            strokeWidth={1}
          />

          {stationTicks.map((s) => {
            const x = PADDING + s * scaleX;
            return (
              <Group key={`tick-${s}`}>
                <Line points={[x, drawAreaTop, x, drawAreaTop + drawHeight]} stroke="#2a2a3e" strokeWidth={1} />
                <Text x={x - 15} y={drawAreaTop + drawHeight + 4} text={`${s}`} fontSize={10} fill="#666" fontFamily="monospace" />
              </Group>
            );
          })}

          {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
            const perimVal = frac * unwrapped.maxPerimeter;
            const y = drawAreaTop + drawHeight - frac * drawHeight;
            return (
              <Group key={`peri-${frac}`}>
                <Line points={[PADDING, y, PADDING + drawWidth, y]} stroke="#2a2a3e" strokeWidth={frac === 0 ? 1 : 0.5} />
                <Text x={4} y={y - 5} text={`${perimVal.toFixed(1)}m`} fontSize={9} fill="#666" fontFamily="monospace" />
              </Group>
            );
          })}

          <Line
            points={unwrapped.rings.flatMap((r) => [
              PADDING + r.station * scaleX,
              drawAreaTop + drawHeight - r.perimeter * scaleY,
            ])}
            stroke="#00ff88"
            strokeWidth={1.5}
            opacity={0.8}
          />

          <Line
            points={[PADDING, drawAreaTop + drawHeight, PADDING + drawWidth, drawAreaTop + drawHeight]}
            stroke="#00ff88"
            strokeWidth={1.5}
            opacity={0.8}
          />

          {unwrapped.rings.filter((_, i) => i % 5 === 0).map((r, i) => {
            const x = PADDING + r.station * scaleX;
            const yTop = drawAreaTop + drawHeight - r.perimeter * scaleY;
            const yBot = drawAreaTop + drawHeight;
            return (
              <Line key={`ring-${i}`} points={[x, yTop, x, yBot]} stroke={r.color} strokeWidth={0.5} opacity={0.3} />
            );
          })}

          <Text x={PADDING + drawWidth / 2 - 30} y={drawAreaTop + drawHeight + 18} text="Stasjon (m)" fontSize={11} fill="#888" fontFamily="system-ui" />
          <Text x={4} y={drawAreaTop + drawHeight / 2 - 30} text="Perimeter (m)" fontSize={11} fill="#888" fontFamily="system-ui" rotation={-90} />
        </Layer>

        {/* Drawing layer */}
        <Layer>
          {lines.map((line) => (
            <Line
              key={line.id}
              points={lineToPixels(line)}
              stroke={selectedLineId === line.id ? "#ffff00" : line.color}
              strokeWidth={selectedLineId === line.id ? 3 : 2}
              lineCap="round"
              lineJoin="round"
              hitStrokeWidth={12}
              onClick={(e) => {
                if (tool === "select") {
                  e.cancelBubble = true;
                  setSelectedLineId(line.id);
                }
              }}
            />
          ))}

          {currentLinePixels && currentLinePixels.length >= 2 && (
            <Line
              points={currentLinePixels}
              stroke="#ff4444"
              strokeWidth={2}
              lineCap="round"
              lineJoin="round"
              opacity={0.8}
              dash={[6, 3]}
            />
          )}
        </Layer>
      </Stage>
    </div>
  );
}
